import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  ReactNode,
} from "react";
import {
  ApplicationFormData,
  RecipientInfo,
  BuildingInfo,
  EquipmentInfo,
  DocumentFile,
  Obligations,
  initialFormData,
  initialRecipientInfo,
  getRequiredDocuments,
  validateEDRPOU,
  validateIBAN,
} from "@/lib/application";
import { useProfile } from "./ProfileContext";
import { useAuth } from "./AuthContext";
import {
  createApplicationWithLog,
  updateApplication,
  getApplication,
  saveEquipmentTypes,
  mapRecordToFormData,
} from "@/lib/api/applications";

interface ApplicationFormContextValue {
  formData: ApplicationFormData;
  currentStep: number;
  totalSteps: number;
  setCurrentStep: (step: number) => void;
  updateRecipient: (data: Partial<RecipientInfo>) => void;
  updateBuilding: (data: Partial<BuildingInfo>) => void;
  updateEquipment: (data: Partial<EquipmentInfo>) => void;
  updateDocuments: (documents: DocumentFile[]) => void;
  updateObligations: (data: Partial<Obligations>) => void;
  resetForm: () => void;
  canProceed: (step: number) => boolean;
  loadedFromProfile: boolean;
  resetRecipientToProfile: () => void;
  // New: Draft management
  applicationId: number | null;
  applicationNumber: string | null;  // Unique 8-digit public ID
  userSeq: number | null;            // Per-user sequence number (1, 2, 3...)
  isSaving: boolean;
  lastSaved: Date | null;
  saveDraft: () => Promise<void>;
  loadApplication: (id: string | number) => Promise<boolean>;
  isLoading: boolean;
}

const ApplicationFormContext = createContext<ApplicationFormContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components
export function useApplicationForm() {
  const context = useContext(ApplicationFormContext);
  if (!context) {
    throw new Error("useApplicationForm must be used within ApplicationFormProvider");
  }
  return context;
}

interface ApplicationFormProviderProps {
  children: ReactNode;
  initialApplicationId?: string;
}

export function ApplicationFormProvider({ children, initialApplicationId }: ApplicationFormProviderProps) {
  const { profile, hasStoredProfile, idGovFields } = useProfile();
  const { user } = useAuth();
  const [formData, setFormData] = useState<ApplicationFormData>(initialFormData);
  const [currentStep, setCurrentStepState] = useState(1);
  const [loadedFromProfile, setLoadedFromProfile] = useState(false);
  const [applicationId, setApplicationId] = useState<number | null>(
    initialApplicationId ? Number(initialApplicationId) : null
  );
  const [applicationNumber, setApplicationNumber] = useState<string | null>(null);
  const [userSeq, setUserSeq] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  // Start loading if we have an application ID to load
  const [isLoading, setIsLoading] = useState(!!initialApplicationId);
  const totalSteps = 5;
  
  // Track if we've loaded data to prevent overwriting with profile
  const hasLoadedApplication = useRef(false);
  // Debounce timer for auto-save
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Ref to store latest saveDraft function to avoid stale closures
  const saveDraftRef = useRef<() => Promise<void>>(() => Promise.resolve());

  // Load existing application if ID provided
  useEffect(() => {
    if (initialApplicationId && !hasLoadedApplication.current) {
      loadApplication(initialApplicationId);
    }
  }, [initialApplicationId]);

  // Load from profile on mount (only if not editing existing application)
  useEffect(() => {
    if (hasStoredProfile && !loadedFromProfile && !hasLoadedApplication.current && !initialApplicationId) {
      setFormData((prev) => ({
        ...prev,
        recipient: {
          recipient_type: profile.recipient_type,
          organization_name: profile.organization_name,
          edrpou_code: profile.edrpou_code,
          legal_address: profile.legal_address,
          actual_address: profile.actual_address || profile.legal_address,
          phone: profile.phone,
          email: profile.email,
          iban: profile.iban,
          head_full_name: profile.head_full_name,
        },
      }));
      setLoadedFromProfile(true);
    }
  }, [hasStoredProfile, profile, loadedFromProfile, initialApplicationId]);

  // Auto-save debounced - uses ref to always call latest saveDraft
  const debouncedSave = useCallback(
    () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      saveTimeoutRef.current = setTimeout(() => {
        // Use ref to get the latest saveDraft function
        saveDraftRef.current();
      }, 2000); // Save 2 seconds after last change
    },
    []
  );

  // Load existing application
  const loadApplication = async (id: string | number): Promise<boolean> => {
    setIsLoading(true);
    console.log("=== LOADING APPLICATION ===");
    console.log("Application ID:", id);
    try {
      const record = await getApplication(id);
      console.log("=== RAW RECORD FROM API ===");
      console.log("Full record:", record);
      if (record) {
        console.log("=== RECORD FIELDS ===");
        console.log("Recipient fields:", {
          recipient_type: record.recipient_type,
          organization_name: record.organization_name,
          edrpou_code: record.edrpou_code,
          head_full_name: record.head_full_name,
          legal_address: record.legal_address,
          actual_address: record.actual_address,
          phone: record.phone,
          email: record.email,
          iban: record.iban,
        });
        console.log("Building fields:", {
          building_address: record.building_address,
          floors_count: record.floors_count,
          boiler_room_type: record.boiler_room_type as import("@/lib/application/types").BoilerRoomType,
          has_tech_passport: record.has_tech_passport,
          co_owners_count: record.co_owners_count,
          apartments_count: record.apartments_count,
          total_area: record.total_area,
        });
        console.log("Equipment fields:", {
          equipment: record.equipment,
          has_supply_contract: record.has_supply_contract,
          contract_number: record.contract_number,
          contract_date: record.contract_date,
          supplier_name: record.supplier_name,
          supplier_edrpou: record.supplier_edrpou,
          equipment_description: record.equipment_description,
          equipment_cost: record.equipment_cost,
          delivery_deadline: record.delivery_deadline,
        });
        console.log("Documents:", record.documents);
        console.log("Obligations:", {
          obligations_accepted: record.obligations_accepted,
          consent_data_processing: record.consent_data_processing,
          consent_terms: record.consent_terms,
        });
        console.log("Current step:", record.current_step);
        
        const data = mapRecordToFormData(record);
        console.log("=== MAPPED FORM DATA ===");
        console.log("Recipient:", data.recipient);
        console.log("Building:", data.building);
        console.log("Equipment:", data.equipment);
        console.log("Documents:", data.documents);
        console.log("Obligations:", data.obligations);
        
        setFormData(data);
        setCurrentStepState(record.current_step || 1);
        setApplicationId(record.id);
        setApplicationNumber(record.application_number);
        setUserSeq(record.user_seq);
        hasLoadedApplication.current = true;
        console.log("=== APPLICATION LOADED SUCCESSFULLY ===");
        return true;
      }
      console.log("=== NO RECORD FOUND ===");
      return false;
    } catch (error) {
      console.error("Failed to load application:", error);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Save draft to server
  const saveDraft = useCallback(async () => {
    // No user - can't save
    if (!user?.id) {
      console.log("Skipping save - no authenticated user");
      return;
    }
    
    // Need EDRPOU for application numbering
    const edrpou = user.edrpoucode;
    if (!edrpou) {
      console.error("Cannot save - user has no EDRPOU code");
      return;
    }

    console.log("=== SAVING DRAFT ===");
    console.log("User ID:", user.id);
    console.log("EDRPOU:", edrpou);
    console.log("Application ID:", applicationId);
    console.log("Current step:", currentStep);
    console.log("Form data to save:", {
      recipient: formData.recipient,
      building: formData.building,
      equipment: formData.equipment,
      obligations: formData.obligations,
    });

    setIsSaving(true);
    try {
      if (applicationId) {
        // Update existing
        console.log("Updating existing application:", applicationId);
        const updateResult = await updateApplication(applicationId, formData, currentStep);
        console.log("Update result:", updateResult);
        // Save equipment types separately
        await saveEquipmentTypes(applicationId, formData.equipment.equipment_types);
      } else {
        // Create new - pass EDRPOU for numbering (e.g., 44556677-01)
        // Uses createApplicationWithLog which records status history and audit log
        console.log("Creating new application for user:", user.id, "EDRPOU:", edrpou);
        const record = await createApplicationWithLog(user.id, edrpou, formData);
        console.log("Created application:", record);
        if (record) {
          setApplicationId(record.id);
          setApplicationNumber(record.application_number);
          setUserSeq(record.user_seq);
          // Save equipment types
          await saveEquipmentTypes(record.id, formData.equipment.equipment_types);
        }
      }
      setLastSaved(new Date());
      console.log("Draft saved successfully");
    } catch (error) {
      console.error("Failed to save draft:", error);
    } finally {
      setIsSaving(false);
    }
  }, [applicationId, formData, currentStep, user]);

  // Keep saveDraftRef updated with latest function
  useEffect(() => {
    saveDraftRef.current = saveDraft;
  }, [saveDraft]);

  // Wrapper for setCurrentStep that also saves
  const setCurrentStep = useCallback((step: number) => {
    setCurrentStepState(step);
    // Auto-save when changing steps
    debouncedSave();
  }, [debouncedSave]);

  const updateRecipient = useCallback((data: Partial<RecipientInfo>) => {
    setFormData((prev) => ({
      ...prev,
      recipient: { ...prev.recipient, ...data },
    }));
    debouncedSave();
  }, [debouncedSave]);

  const updateBuilding = useCallback((data: Partial<BuildingInfo>) => {
    setFormData((prev) => ({
      ...prev,
      building: { ...prev.building, ...data },
    }));
    debouncedSave();
  }, [debouncedSave]);

  const updateEquipment = useCallback((data: Partial<EquipmentInfo>) => {
    setFormData((prev) => ({
      ...prev,
      equipment: { ...prev.equipment, ...data },
    }));
    debouncedSave();
  }, [debouncedSave]);

  const updateDocuments = useCallback((documents: DocumentFile[]) => {
    setFormData((prev) => ({
      ...prev,
      documents,
    }));
    debouncedSave();
  }, [debouncedSave]);

  const updateObligations = useCallback((data: Partial<Obligations>) => {
    setFormData((prev) => ({
      ...prev,
      obligations: { ...prev.obligations, ...data },
    }));
    debouncedSave();
  }, [debouncedSave]);

  const resetForm = useCallback(() => {
    setFormData(initialFormData);
    setCurrentStepState(1);
    setLoadedFromProfile(false);
    setApplicationId(null);
    setApplicationNumber(null);
    setUserSeq(null);
    setLastSaved(null);
    hasLoadedApplication.current = false;
  }, []);

  const resetRecipientToProfile = useCallback(() => {
    if (hasStoredProfile) {
      setFormData((prev) => ({
        ...prev,
        recipient: {
          recipient_type: profile.recipient_type,
          organization_name: profile.organization_name,
          edrpou_code: profile.edrpou_code,
          legal_address: profile.legal_address,
          actual_address: profile.actual_address || profile.legal_address,
          phone: profile.phone,
          email: profile.email,
          iban: profile.iban,
          head_full_name: profile.head_full_name,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        recipient: initialRecipientInfo,
      }));
    }
    debouncedSave();
  }, [hasStoredProfile, profile, debouncedSave]);

  const canProceed = useCallback(
    (step: number) => {
      switch (step) {
        case 1: {
          const r = formData.recipient;
          // For EDRPOU: if from ID.GOV.UA - just check format (it's verified)
          // If manually entered - validate checksum
          const edrpouValid = idGovFields.edrpou_code
            ? /^\d{8}$/.test(r.edrpou_code)  // ID.GOV.UA - format only
            : validateEDRPOU(r.edrpou_code).valid;  // Manual - full validation
          
          // IBAN always requires checksum validation (never from ID.GOV.UA)
          const ibanValid = validateIBAN(r.iban).valid;
          
          const checks = {
            recipient_type: r.recipient_type !== null,
            organization_name: r.organization_name.length >= 3,
            edrpou_code: edrpouValid,
            legal_address: r.legal_address.length >= 10,
            phone: /^\+380\d{9}$/.test(r.phone.replace(/\s/g, "")),
            email: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(r.email),
            iban: ibanValid,
            head_full_name: r.head_full_name.length >= 5,
          };
          
          // Debug log to help identify failing validation
          const failingFields = Object.entries(checks).filter(([, valid]) => !valid).map(([field]) => field);
          if (failingFields.length > 0) {
            console.log("[Step 1 Validation] Failing fields:", failingFields, "Values:", {
              recipient_type: r.recipient_type,
              organization_name: r.organization_name,
              edrpou_code: r.edrpou_code,
              legal_address: r.legal_address,
              phone: r.phone,
              email: r.email,
              iban: r.iban,
              head_full_name: r.head_full_name,
            });
          }
          
          return Object.values(checks).every(Boolean);
        }
        case 2: {
          const b = formData.building;
          return (
            b.building_address.length >= 10 &&
            b.floors_count !== null &&
            b.floors_count >= 1 &&
            b.co_owners_count !== null &&
            b.co_owners_count >= 1 &&
            b.apartments_count !== null &&
            b.apartments_count >= 1 &&
            b.total_area !== null &&
            b.total_area > 0
          );
        }
        case 3: {
          const e = formData.equipment;
          if (e.equipment_types.length === 0) return false;
          if (e.has_supply_contract) {
            // Supplier EDRPOU is always manually entered, validate with checksum
            const supplierEdrpouValid = validateEDRPOU(e.supplier_edrpou).valid;
            return (
              e.contract_number.length > 0 &&
              e.contract_date.length > 0 &&
              e.supplier_name.length > 0 &&
              supplierEdrpouValid &&
              e.equipment_description.length > 0 &&
              e.equipment_cost !== null &&
              e.equipment_cost > 0 &&
              e.delivery_deadline.length > 0
            );
          }
          return true;
        }
        case 4: {
          // Check if all REQUIRED documents are uploaded (not optional ones)
          const requiredDocs = formData.recipient.recipient_type
            ? getRequiredDocuments(
                formData.recipient.recipient_type,
                formData.building.boiler_room_type,
                formData.building.has_tech_passport
              )
            : [];
          
          // Check that each required document type has been uploaded
          return requiredDocs.length > 0 && requiredDocs.every((docType) => {
            const doc = formData.documents.find((d) => d.type === docType);
            return doc?.uploaded === true;
          });
        }
        case 5: {
          const o = formData.obligations;
          return (
            o.obligations_accepted &&
            o.consent_data_processing &&
            o.consent_terms
          );
        }
        default:
          return false;
      }
    },
    [formData]
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  return (
    <ApplicationFormContext.Provider
      value={{
        formData,
        currentStep,
        totalSteps,
        setCurrentStep,
        updateRecipient,
        updateBuilding,
        updateEquipment,
        updateDocuments,
        updateObligations,
        resetForm,
        canProceed,
        loadedFromProfile,
        resetRecipientToProfile,
        // Draft management
        applicationId,
        applicationNumber,
        userSeq,
        isSaving,
        lastSaved,
        saveDraft,
        loadApplication,
        isLoading,
      }}
    >
      {children}
    </ApplicationFormContext.Provider>
  );
}
